import React from 'react';
import { TONE_SOFT, toneForStatus, humanizeStatus } from './tone';

/**
 * The state of one row, said the same way everywhere.
 *
 * Two ways to call it. Pass `status` and it resolves the tone and the label
 * from the shared vocabulary, which is what most screens want:
 *
 *   <StatusPill status={row.status} />
 *
 * Or pass `tone` and children when the screen knows better than the map — a
 * pending correction on an otherwise-present attendance row, say, where the
 * label is a sentence rather than a status name:
 *
 *   <StatusPill tone="warning">Correction pending</StatusPill>
 *
 * `uppercase` is off by default. The clones disagreed (two shouted, three did
 * not) and lowercase is the quieter default for a cell that repeats down a
 * column.
 *
 * Shape is a pill, matching Sales and Management (`rounded-full px-2.5 py-1
 * text-[13px] font-semibold`). A badge is the one round-ended thing in the old
 * screens — controls there are rectangles — so this is the exception that
 * proves the radius rule rather than a contradiction of it.
 *
 * `dense` keeps the pill shape but drops to the compact padding the newer
 * screens were using. Table cells need it: at the full size a status column
 * adds roughly 8px to every row, which on a 200-row muster roll is a screen
 * and a half of scrolling bought for nothing.
 */
const StatusPill = ({
  status,
  tone,
  children,
  uppercase = false,
  dense = false,
  className = ''
}) => {
  const resolved = tone ?? toneForStatus(status);
  const label = children ?? humanizeStatus(status);

  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-pill ${
        dense ? 'px-2 py-0.5 text-xs font-medium' : 'px-2.5 py-1 text-[13px] font-semibold'
      } ${TONE_SOFT[resolved] ?? TONE_SOFT.neutral} ${
        uppercase ? 'uppercase tracking-wide' : ''
      } ${className}`}>
      {label}
    </span>
  );
};

export default StatusPill;
